import React from 'react';

/**
 * One turning gear, sized to sit inline with text -- the app's small
 * "working on it" indicator, wired up app-wide through
 * `Spin.setDefaultIndicator` in index.tsx, so every `<Spin>` everywhere
 * (page loaders, `size="small"` inline spinners, `<Spin spinning>`
 * overlays) renders this with no per-call-site change.
 *
 * Why ONE gear and not the two-gear piece GearLoader draws: at the size an
 * inline indicator actually gets -- 14px on `size="small"`, 20px by default
 * -- the pair renders as a grey smudge, teeth and all, and the small gear
 * lands at about 6px. Checked in Chromium at 14/20/32/48px rather than
 * assumed. This is the same gear, the big one's own geometry lifted
 * untouched (centre 290,180, r80, eight 30x40 teeth, r30 hole), cropped to
 * its own bounding box so the drawing fills the box with nothing to trim.
 *
 * Two deliberate differences from the illustration, both because this is an
 * indicator rather than scenery:
 *
 * - It inherits `currentColor` instead of carrying the artwork's own pale
 *   grey at 0.6 opacity. That grey is right behind a loading card and all
 *   but invisible as a 14px glyph, and an indicator has to work on a coral
 *   button, a teal one and a cream panel alike -- which is exactly what
 *   antd's own dots do.
 * - The centre is a real hole, punched with fill-rule="evenodd" on one
 *   path rather than covered by a white circle, so a coloured background
 *   shows through it instead of a white dot sitting on top. Done with a
 *   path and not a <mask> on purpose: mask ids are document-global, and
 *   several spinners on one page would share -- then fight over -- a single
 *   id.
 */
const GEAR_TEETH_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];

export interface IGearSpinnerProps extends React.SVGAttributes<SVGSVGElement> {
  /** Anything CSS accepts. Defaults to 1em, which is what antd's own Spin sizing needs -- see below. */
  size?: number | string;
  /** One full turn, in seconds. */
  durationSeconds?: number;
  /** Counter-clockwise, for pairing two of these against each other. */
  reverse?: boolean;
  /**
   * Passed in by antd's Spin Indicator wrapper, which clones this element
   * -- not a valid DOM attribute, so it is destructured out here instead of
   * being spread onto the <svg>, which would earn a React "unknown prop"
   * warning.
   */
  percent?: number;
}

const GearSpinner: React.FC<IGearSpinnerProps> = ({
  size = '1em',
  durationSeconds = 2.2,
  reverse = false,
  percent: _percent,
  style,
  ...rest
}) => (
  // 1em by default because antd clones this with an `ant-spin-dot`
  // className, and THAT class -- not any prop -- is what carries the real
  // font-size for each Spin size. An em-based box therefore scales itself
  // between small/default/large without this component ever knowing which
  // variant it is in.
  <svg
    {...rest}
    width={size}
    height={size}
    viewBox="182 72 216 216"
    role="img"
    aria-hidden="true"
    style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}
  >
    <style>{`
      @keyframes gear-spinner-turn { to { transform: rotate(360deg); } }
      @keyframes gear-spinner-turn-reverse { to { transform: rotate(-360deg); } }
    `}</style>
    <g
      style={{
        transformOrigin: '290px 180px',
        animation: `${reverse ? 'gear-spinner-turn-reverse' : 'gear-spinner-turn'} ${durationSeconds}s linear infinite`,
        fill: 'currentColor',
      }}
    >
      {/* Body and hole in one path: the outer circle, then the inner one,
          with evenodd making the second subtract from the first. */}
      <path
        fillRule="evenodd"
        d="M210,180 a80,80 0 1,0 160,0 a80,80 0 1,0 -160,0 Z M260,180 a30,30 0 1,0 60,0 a30,30 0 1,0 -60,0 Z"
      />
      {GEAR_TEETH_ANGLES.map((angle) => (
        <rect key={angle} x="275" y="72" width="30" height="40" rx="8" transform={`rotate(${angle} 290 180)`} />
      ))}
    </g>
  </svg>
);

export default GearSpinner;
